import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

/**
 * Listens for the `fias:session-expired` window event and shows a
 * modal with a Sign In button that returns the user to their current
 * location after re-authentication.
 *
 * Replaces the previous behavior where the only recovery from a
 * silently-expired session was for the user to manually log out and
 * back in. The event is dispatched from:
 *
 *   - `lib/api.ts` 401 interceptor when refresh fails to recover
 *   - `AuthContext` proactive timer when refresh-token has expired
 *   - `useStreaming` / `useMultiStreaming` when the WS ticket 401s
 *
 * Mount once, inside the router context, near the App root (alongside
 * CreditExhaustionListener). Renders an invisible modal until the
 * event fires.
 */
export function SessionExpiredListener() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handler = () => {
      // Skip on /auth/* — showing a "session expired" modal over the
      // login form is jarring and adds nothing useful.
      if (location.pathname.startsWith('/auth/')) return;
      setOpen(true);
    };

    window.addEventListener('fias:session-expired', handler);
    return () => {
      window.removeEventListener('fias:session-expired', handler);
    };
  }, [location.pathname]);

  const handleSignIn = () => {
    setOpen(false);
    // LoginPage reads `location.state.from` to redirect after login
    // (see auth/pages/LoginPage.tsx:20). Preserving search params
    // means the user lands back on the same filtered/paginated view
    // they were working on.
    navigate('/auth/login', {
      replace: true,
      state: { from: location.pathname + location.search },
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Your session has expired</AlertDialogTitle>
          <AlertDialogDescription>
            For your security, we sign you out after a period of inactivity. Sign in again to pick
            up where you left off — your work is saved.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={handleSignIn}>Sign in</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
